// native
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';

// service
import { BaaAgreementService } from '../../core/services/baa-agreement.service';
import { AuthService } from '../../core/services/auth.service';
import { ErrorService } from '../../core/services/error.service';

// constants
import { AUTH_STORAGE_KEYS } from '../../constants';

@Component({
    selector: 'app-baa-agreement',
    templateUrl: './baa-agreement.component.html',
    standalone: false
})
export class BaaAgreementComponent implements OnInit {

  /** Language code of the English source BAA — the only language the agreement is shown in this iteration. */
  private static readonly ENGLISH_LANGUAGE = 'en-us';

  /**
   * Sections rendered from the agreement. Section 8 (Controlling Language) exists in en-us.json
   * but is deliberately NOT rendered this iteration: it only applies to translated versions of
   * the agreement, and the BAA is currently English-only. Include 8 here when translations return.
   */
  private static readonly SECTION_NUMBERS = [1, 2, 3, 4, 5, 6, 7];

  readonly sectionNumbers = BaaAgreementComponent.SECTION_NUMBERS;

  isLoading: boolean = false;

  /** True only when every required piece of the English agreement text has loaded and validated. */
  isReady: boolean = false;

  /** True when loading the English agreement text failed; enables the retry state. */
  loadFailed: boolean = false;

  /**
   * The English agreement text, held on the component and rendered directly. The ngx-translate
   * store is intentionally NOT used for the document text: the app points both the active and the
   * default language at the user's own language, and language (re)loads replace a language's store
   * wholesale — state injected there can be erased by an in-flight load, and a later language
   * switch would drop it entirely. Holding the text here makes the rendered agreement immune to
   * translation-store races and language changes. UI chrome (buttons, error toast) stays localized
   * through the normal translate pipeline.
   */
  baaText: { [key: string]: string } = {};

  constructor(
    private baaAgreementService: BaaAgreementService,
    private authService: AuthService,
    private router: Router,
    private translateService: TranslateService,
    private errorService: ErrorService
  ) { }

  ngOnInit(): void {
    this.loadBaaText();
  }

  loadBaaText(): void {
    this.isReady = false;
    this.loadFailed = false;
    this.translateService.getTranslation(BaaAgreementComponent.ENGLISH_LANGUAGE).subscribe(
      dictionary => {
        const text: { [key: string]: string } = {};
        Object.keys(dictionary || {})
          .filter(key => key.startsWith('baa.'))
          .forEach(key => (text[key] = dictionary[key]));
        if (this.hasCompleteAgreementText(text)) {
          this.baaText = text;
          this.isReady = true;
        } else {
          this.loadFailed = true;
        }
      },
      () => this.loadFailed = true
    );
  }

  /**
   * The user must never be able to accept an agreement that rendered blank or partially: accept
   * stays disabled unless every required key resolved to real text.
   */
  private hasCompleteAgreementText(text: { [key: string]: string }): boolean {
    const requiredKeys = ['baa.title', 'baa.preamble', 'baa.signatureBlock'];
    for (const section of BaaAgreementComponent.SECTION_NUMBERS) {
      requiredKeys.push(`baa.${section}.title`, `baa.${section}.body`);
    }
    return requiredKeys.every(key => typeof text[key] === 'string' && text[key].trim().length > 0);
  }

  onAgreementAccept() {
    if (!this.isReady || this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.baaAgreementService.sign().pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      response => {
        localStorage.setItem(AUTH_STORAGE_KEYS.SHOW_BAA_AGREEMENT, "false");
        this.router.navigate(['/patients']);
      },
      error => this.errorService.handleError(this.translateService.instant('baa.signingError')),
    );
  }

  onAgreementCancel() {
    this.isLoading = true;
    this.authService.logout().finally(() => this.isLoading = false);
  }
}
